import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BlockButton as UntypedBlockButton } from "../../inJoy-components/Button/BlockButton";
import { IconButton } from "../../inJoy-components/Button/IconButton";
import { Icon } from "../../inJoy-components/Icon/Icon";
import { Input as UntypedInput } from "../../inJoy-components/Input/Input";
import type { AgentStatus } from "../../redux/events/events-types";
import { isValidEmail } from "../../utils/validate-email";
import { InjoyDesktopDatePicker } from "../InjoyDatePicker/InjoyDesktopDatePicker";
import { AddCameraModal } from "./AddCameraModal";
import { CourtLinesUpload } from "./CourtLinesUpload";

// Workaround for untyped components
const Input = UntypedInput as any;
const BlockButton = UntypedBlockButton as any;

/**
 * Format a Date into a 12-hour time label, e.g. "1:00 pm", "12:15 am".
 */
const formatTime12h = (d: Date): string => {
	let h = d.getHours();
	const m = d.getMinutes();
	const period = h >= 12 ? "pm" : "am";
	h = h % 12 || 12;
	return `${h}:${m.toString().padStart(2, "0")} ${period}`;
};

/**
 * Build 15-minute time slots from `start` up to (but not including) `end`.
 * Each slot is returned as a formatted 12-hour string.
 */
const buildTimeSlots = (start: Date, end: Date): string[] => {
	const slots: string[] = [];
	const cursor = new Date(start);
	while (cursor < end) {
		slots.push(formatTime12h(cursor));
		cursor.setMinutes(cursor.getMinutes() + 15);
	}
	return slots;
};

interface EventCreationFormProps {
	agents: AgentStatus[];
	isLoading: boolean;
	onClose?: () => void;
	onSubmit?: (data: EventFormData) => void;
	onRefreshAgents?: () => void;
}

export interface EventFormData {
	eventTitle: string;
	hostEmail: string;
	hostName: string;
	date: Date;
	startTime: string;
	endTime: string;
	agentId: string;
	calibrationFile: File | null;
	/** Whether user explicitly changed the calibration (upload or remove) */
	calibrationChanged: boolean;
}

/**
 * Round a Date up to the next 15-minute boundary.
 * e.g. 14:03 → 14:15, 14:15 → 14:15, 14:46 → 15:00
 */
const ceilTo15 = (d: Date): Date => {
	const out = new Date(d);
	const mins = out.getMinutes();
	const remainder = mins % 15;
	if (remainder !== 0) {
		out.setMinutes(mins + (15 - remainder), 0, 0);
	} else {
		out.setSeconds(0, 0);
	}
	return out;
};

const StatusDot = ({ healthy }: { healthy: boolean }) => (
	<span
		className="inline-block w-[8px] h-[8px] rounded-full shrink-0"
		style={{ backgroundColor: healthy ? "#21C651" : "#FFB600" }}
	/>
);

interface CameraSelectProps {
	agents: AgentStatus[];
	value: string;
	disabled: boolean;
	label: string;
	placeholder: string;
	noAgentHint: string;
	addCameraLabel: string;
	onChange: (agentId: string) => void;
	onAddCamera: () => void;
}

const CameraSelect = ({
	agents,
	value,
	disabled,
	label,
	placeholder,
	noAgentHint,
	addCameraLabel,
	onChange,
	onAddCamera,
}: CameraSelectProps) => {
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const selected = agents.find((a) => a.agent_id === value);

	useEffect(() => {
		if (!open) return;
		const handleClickOutside = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [open]);

	// Empty state: no agents available
	if (agents.length === 0) {
		return (
			<div className="flex flex-col gap-[6px] items-start w-full">
				<label className="flex items-end gap-[2px] w-full Subtitle2">
					<span className="text-[#404040]">{label}</span>
					<span className="text-[#e90035]">*</span>
				</label>
				<p className="text-[14px] font-normal leading-[1.4] tracking-[0.25px] text-[#6d6d6d]">
					{noAgentHint}
				</p>
				<button
					type="button"
					className="flex items-center gap-[4px]"
					onClick={onAddCamera}
				>
					<Icon iconName="plus_outline" size="Small" color="#0060e5" />
					<span className="text-[14px] font-medium leading-[1.25] text-[#0060e5]">
						{addCameraLabel}
					</span>
				</button>
			</div>
		);
	}

	return (
		<div
			className="flex flex-col gap-[8px] items-start w-full"
			ref={containerRef}
		>
			<label className="flex items-end gap-[2px] w-full Subtitle2">
				<span className="text-[#404040]">{label}</span>
				<span className="text-[#e90035]">*</span>
			</label>
			<div className="relative w-full">
				<button
					type="button"
					disabled={disabled}
					onClick={() => setOpen(!open)}
					className="bg-color_background_grey_1 h-[36px] rounded-[4px] w-full flex items-center p-[8px] gap-[8px]"
				>
					{selected ? (
						<>
							<StatusDot healthy={selected.is_healthy} />
							<span className="flex-1 text-left font-normal leading-[1.4] text-[#404040] text-[14px] tracking-[0.25px] truncate">
								{selected.name} ({selected.desc})
							</span>
						</>
					) : (
						<span className="flex-1 text-left font-normal leading-[1.4] text-[#999] text-[14px] tracking-[0.25px]">
							{placeholder}
						</span>
					)}
					<Icon iconName="arrow_down" size="Small" />
				</button>
				{open && (
					<div className="absolute top-[40px] left-0 right-0 z-10 bg-white border border-[#e9eaeb] rounded-[8px] shadow-[0px_12px_16px_-4px_rgba(10,13,18,0.08),0px_4px_6px_-2px_rgba(10,13,18,0.03)] flex flex-col max-h-[230px]">
						<div className="py-[4px] overflow-y-auto">
							{agents.map((agent) => (
								<button
									key={agent.agent_id}
									type="button"
									onClick={() => {
										onChange(agent.agent_id);
										setOpen(false);
									}}
									className={`w-full flex items-center gap-[8px] px-[14px] py-[12px] hover:bg-[#f5f5f5] text-left ${agent.agent_id === value ? "bg-[#f0f5ff]" : ""}`}
								>
									<StatusDot healthy={agent.is_healthy} />
									<span className="flex-1 font-medium leading-[1.35] text-[#404040] text-[16px] truncate">
										{agent.name}
									</span>
								</button>
							))}
						</div>
						<div className="border-t border-[#dfdfdf] py-[8px]">
							<button
								type="button"
								onClick={() => {
									setOpen(false);
									onAddCamera();
								}}
								className="w-full flex items-center gap-[8px] px-[14px] py-[12px] hover:bg-[#f5f5f5] text-left"
							>
								<Icon iconName="plus_outline" size="Medium" color="#6d6d6d" />
								<span className="font-medium leading-[1.35] text-[#404040] text-[16px]">
									{addCameraLabel}
								</span>
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export const EventCreationForm = ({
	agents,
	isLoading,
	onClose,
	onSubmit,
	onRefreshAgents,
}: EventCreationFormProps) => {
	const { t } = useTranslation();
	const now = useMemo(() => new Date(), []);
	const [emailError, setEmailError] = useState<string | null>(null);
	const [showAddCamera, setShowAddCamera] = useState(false);

	// Earliest selectable start: next 15-min boundary from now
	const initialStart = useMemo(() => ceilTo15(now), [now]);

	const [formData, setFormData] = useState<EventFormData>(() => {
		const start = formatTime12h(initialStart);
		// Default end = nearest 15-min slot to now's time-of-day,
		// i.e. the first slot after start
		const firstEnd = new Date(initialStart);
		firstEnd.setMinutes(firstEnd.getMinutes() + 15);
		return {
			eventTitle: "",
			hostEmail: "",
			hostName: "",
			date: now,
			startTime: start,
			endTime: formatTime12h(firstEnd),
			agentId: "",
			calibrationFile: null,
			calibrationChanged: false,
		};
	});

	// Auto-select first agent when agents load
	useEffect(() => {
		if (!formData.agentId && agents.length > 0) {
			setFormData((prev) => ({ ...prev, agentId: agents[0].agent_id }));
		}
	}, [agents, formData.agentId]);

	// Is the selected date today?
	const isToday = useMemo(() => {
		const d = formData.date;
		return (
			d.getFullYear() === now.getFullYear() &&
			d.getMonth() === now.getMonth() &&
			d.getDate() === now.getDate()
		);
	}, [formData.date, now]);

	// Start-time options: 15-min slots from earliest available to 23:45
	const startTimeOptions = useMemo(() => {
		const dayStart = isToday
			? initialStart
			: new Date(
					formData.date.getFullYear(),
					formData.date.getMonth(),
					formData.date.getDate(),
					0,
					0,
				);
		const dayEnd = new Date(
			formData.date.getFullYear(),
			formData.date.getMonth(),
			formData.date.getDate(),
			23,
			45,
		);
		// include 23:45 itself
		dayEnd.setMinutes(dayEnd.getMinutes() + 1);
		return buildTimeSlots(dayStart, dayEnd);
	}, [formData.date, isToday, initialStart]);

	// End-time options: 15-min slots starting one step after the selected start
	const endTimeOptions = useMemo(() => {
		// Parse selected start time back to a Date on the same day
		const idx = startTimeOptions.indexOf(formData.startTime);
		const base = new Date(
			formData.date.getFullYear(),
			formData.date.getMonth(),
			formData.date.getDate(),
		);

		if (idx !== -1) {
			// Reconstruct the start Date by stepping 15-min increments
			const firstSlot = isToday ? initialStart : base;
			const startDate = new Date(firstSlot);
			startDate.setMinutes(startDate.getMinutes() + idx * 15);
			// End options begin one slot after start, run until midnight
			const afterStart = new Date(startDate);
			afterStart.setMinutes(afterStart.getMinutes() + 15);
			const midnight = new Date(base);
			midnight.setDate(midnight.getDate() + 1);
			return buildTimeSlots(afterStart, midnight);
		}
		return [];
	}, [
		formData.startTime,
		formData.date,
		startTimeOptions,
		isToday,
		initialStart,
	]);

	// When the date changes, keep start/end as close to current values as possible
	const handleDateChange = (d: Date) => {
		const isSameDay =
			d.getFullYear() === now.getFullYear() &&
			d.getMonth() === now.getMonth() &&
			d.getDate() === now.getDate();

		const dayStart = isSameDay
			? initialStart
			: new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0);
		const dayEnd = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 46);
		const newStartOptions = buildTimeSlots(dayStart, dayEnd);

		// Pick nearest start
		const prevStart = formData.startTime;
		const startIdx = newStartOptions.indexOf(prevStart);
		const start =
			startIdx !== -1
				? prevStart
				: newStartOptions[
						Math.min(
							Math.max(0, startTimeOptions.indexOf(prevStart)),
							newStartOptions.length - 1,
						)
					] || newStartOptions[0];

		// Build end options from the chosen start
		const chosenStartIdx = newStartOptions.indexOf(start);
		const startDate = new Date(dayStart);
		startDate.setMinutes(startDate.getMinutes() + chosenStartIdx * 15);
		const afterStart = new Date(startDate);
		afterStart.setMinutes(afterStart.getMinutes() + 15);
		const midnight = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
		const newEndOptions = buildTimeSlots(afterStart, midnight);

		// Pick nearest end
		const prevEnd = formData.endTime;
		let endTime: string;
		if (newEndOptions.includes(prevEnd)) {
			endTime = prevEnd;
		} else if (newEndOptions.length > 0) {
			const prevEndIdx = endTimeOptions.indexOf(prevEnd);
			const clamped = Math.min(prevEndIdx, newEndOptions.length - 1);
			endTime = newEndOptions[Math.max(0, clamped)];
		} else {
			endTime = start;
		}

		setFormData({ ...formData, date: d, startTime: start, endTime });
	};

	// When start time changes, keep the end time as close to the current value as possible
	const handleStartTimeChange = (newStart: string) => {
		const idx = startTimeOptions.indexOf(newStart);
		const base = new Date(
			formData.date.getFullYear(),
			formData.date.getMonth(),
			formData.date.getDate(),
		);
		const firstSlot = isToday ? initialStart : base;
		const startDate = new Date(firstSlot);
		startDate.setMinutes(startDate.getMinutes() + idx * 15);

		// Build the new end options
		const afterStart = new Date(startDate);
		afterStart.setMinutes(afterStart.getMinutes() + 15);
		const midnight = new Date(base);
		midnight.setDate(midnight.getDate() + 1);
		const newEndOptions = buildTimeSlots(afterStart, midnight);

		// Pick the nearest option to the current end time
		const prev = formData.endTime;
		let endTime: string;
		if (newEndOptions.includes(prev)) {
			endTime = prev;
		} else if (newEndOptions.length > 0) {
			// Find the closest slot by index (options are chronological)
			const prevIdx = endTimeOptions.indexOf(prev);
			const clamped = Math.min(prevIdx, newEndOptions.length - 1);
			endTime = newEndOptions[Math.max(0, clamped)];
		} else {
			endTime = newStart;
		}

		setFormData({ ...formData, startTime: newStart, endTime });
	};

	const handleSubmit = () => {
		if (formData.hostEmail && !isValidEmail(formData.hostEmail)) {
			setEmailError(t("common.errors.invalid_email"));
			return;
		}
		onSubmit?.(formData);
	};

	const handleCancel = () => {
		onClose?.();
	};

	return (
		<div className="bg-white flex flex-col gap-[32px] items-start overflow-clip px-[24px] py-[32px] rounded-[8px] w-full max-w-[600px]">
			{/* Header */}
			<div className="flex items-center justify-between w-full">
				<p className="flex-1 font-medium leading-[1.2] text-[#404040] text-[24px] tracking-[-0.5px]">
					{t("events.create_dialog.title")}
				</p>
				<IconButton
					type="Tertiary"
					size="Small"
					icon="cross_outline"
					onClick={handleCancel}
				/>
			</div>

			{/* Form Fields */}
			<div className="flex flex-col gap-[12px] items-start w-full">
				{/* Event Title */}
				<Input
					label={
						<div className="flex items-center gap-[2px]">
							<span>{t("events.create_dialog.event_title")}</span>
							<span className="text-[#e90035]">*</span>
						</div>
					}
					placeholder={t("events.create_dialog.event_title_placeholder")}
					value={formData.eventTitle}
					onChange={(e: any) =>
						setFormData({ ...formData, eventTitle: e.target.value })
					}
				/>

				{/* Host Email */}
				<Input
					label={
						<div className="flex items-center gap-[2px]">
							<span>{t("events.create_dialog.host_email")}</span>
							<span className="text-[#e90035]">*</span>
						</div>
					}
					type="email"
					placeholder={t("events.create_dialog.host_email_placeholder")}
					value={formData.hostEmail}
					onChange={(e: any) => {
						setFormData({ ...formData, hostEmail: e.target.value });
						if (emailError) setEmailError(null);
					}}
					onBlur={() => {
						if (formData.hostEmail && !isValidEmail(formData.hostEmail)) {
							setEmailError(t("common.errors.invalid_email"));
						}
					}}
					error={emailError}
					hint={
						!emailError ? t("events.create_dialog.host_email_hint") : undefined
					}
				/>

				{/* Host Name */}
				<Input
					label={t("events.create_dialog.host")}
					placeholder={t("events.create_dialog.host_placeholder")}
					value={formData.hostName}
					onChange={(e: any) =>
						setFormData({ ...formData, hostName: e.target.value })
					}
				/>

				{/* Date */}
				<div className="flex flex-col gap-[8px] items-start w-full">
					<label className="flex items-end gap-[2px] w-full Subtitle2">
						<span className="text-[#404040]">
							{t("events.create_dialog.date")}
						</span>
						<span className="text-[#e90035]">*</span>
					</label>
					<InjoyDesktopDatePicker
						date={formData.date}
						onConfirm={handleDateChange}
					/>
				</div>

				{/* Time Range */}
				<div className="flex gap-[12px] items-end w-full">
					{/* Start Time */}
					<div className="flex flex-1 flex-col gap-[8px] items-start min-w-0">
						<label className="flex items-end gap-[2px] w-full Subtitle2">
							<span className="text-[#404040]">
								{t("events.create_dialog.time")}
							</span>
							<span className="text-[#e90035]">*</span>
						</label>
						<div className="bg-color_background_grey_1 h-[36px] rounded-[4px] w-full">
							<div className="flex items-center overflow-clip rounded-[inherit] size-full">
								<div className="flex items-center p-[8px] size-full">
									<select
										value={formData.startTime}
										onChange={(e) => handleStartTimeChange(e.target.value)}
										className="bg-transparent font-normal leading-[1.4] flex-1 text-[#404040] text-[14px] tracking-[0.25px] outline-none appearance-none"
									>
										{startTimeOptions.map((t) => (
											<option key={t} value={t}>
												{t}
											</option>
										))}
									</select>
									<Icon iconName="arrow_down" size="Small" />
								</div>
							</div>
						</div>
					</div>

					{/* Separator */}
					<div className="flex h-[36px] items-center justify-center shrink-0 text-[14px] text-black tracking-[0.25px] w-[7px]">
						<span>-</span>
					</div>

					{/* End Time */}
					<div className="flex flex-1 flex-col gap-[8px] items-start min-w-0">
						<div className="w-full" />
						<div className="bg-color_background_grey_1 h-[36px] rounded-[4px] w-full">
							<div className="flex items-center overflow-clip rounded-[inherit] size-full">
								<div className="flex items-center p-[8px] size-full">
									<select
										value={formData.endTime}
										onChange={(e) =>
											setFormData({ ...formData, endTime: e.target.value })
										}
										className="bg-transparent font-normal leading-[1.4] flex-1 text-[#404040] text-[14px] tracking-[0.25px] outline-none appearance-none"
									>
										{endTimeOptions.map((t) => (
											<option key={t} value={t}>
												{t}
											</option>
										))}
									</select>
									<Icon iconName="arrow_down" size="Small" />
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="mt-5 w-full flex items-center gap-3">
					<p className="text-[14px] text-color-Grey400">
						{t("events.create_dialog.venue_setting")}
					</p>
					<span className="block flex-1 h-[1px] bg-color-Grey100"></span>
				</div>
				{/* Camera Agent */}
				<CameraSelect
					agents={agents}
					value={formData.agentId}
					disabled={isLoading}
					label={t("events.create_dialog.camera")}
					placeholder={t("events.create_dialog.camera_placeholder")}
					noAgentHint={t("events.create_dialog.no_camera_hint")}
					addCameraLabel={t("events.create_dialog.add_camera")}
					onChange={(agentId) =>
						setFormData({
							...formData,
							agentId,
							calibrationFile: null,
							calibrationChanged: false,
						})
					}
					onAddCamera={() => setShowAddCamera(true)}
				/>

				{/* Court Lines Calibration Upload - shown after camera is selected */}
				{formData.agentId && (
					<CourtLinesUpload
						file={formData.calibrationFile}
						existingCalibration={
							!formData.calibrationChanged
								? agents.find((a) => a.agent_id === formData.agentId)
										?.calibration
								: undefined
						}
						onChange={(file) =>
							setFormData({
								...formData,
								calibrationFile: file,
								calibrationChanged: true,
							})
						}
					/>
				)}

				{/* Action Buttons */}
				<div className="flex gap-[12px] items-center justify-end w-full mt-[20px]">
					<BlockButton
						type="Secondary"
						size="Large"
						width="120px"
						onClick={handleCancel}
						disabled={isLoading}
					>
						{t("events.create_dialog.cancel")}
					</BlockButton>
					<BlockButton
						type="Primary"
						size="Large"
						width="120px"
						onClick={handleSubmit}
						disabled={
							isLoading ||
							!formData.eventTitle ||
							!formData.hostEmail ||
							!formData.agentId
						}
						loading={isLoading}
						loadingIcon="spinner_outline"
					>
						{t("events.create_dialog.create")}
					</BlockButton>
				</div>
			</div>

			<AddCameraModal
				isOpen={showAddCamera}
				onClose={() => setShowAddCamera(false)}
				onCreated={(agentId) => {
					setShowAddCamera(false);
					setFormData((prev) => ({
						...prev,
						agentId,
						calibrationFile: null,
						calibrationChanged: false,
					}));
					onRefreshAgents?.();
				}}
			/>
		</div>
	);
};
